import { useState, useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";

import { useNavigate, useParams } from "react-router-dom";

import useStore from "utils/useStore";

import Button, { Buttons } from "components/Button";
import usePhase from "utils/usePhase";

import constants from "constants/fi.json";

import styles from "./summary.module.css";
import sendOffer from "utils/sendOffer";

import Previous from "components/Previous";

import Transportation from "./transportation";
import Burial from "./burial";
import Coffin from "./coffin";
import Contact from "./contact";
import Preparation from "./preparation";
import Flowers from "./flowers";
import Urn from "./urn";
import ShareSummary from "./ShareSummary";
import SendPdfButton from "./SendPdfButton";
import Deceased from "./deceased";

import Modal from "components/Modal";

import fetchUnfinishedRequest from "utils/fetchUnfinishedRequest";
import submitUnfinishedRequest from "utils/submitUnfinishedRequest";
import updateUnfinishedRequest from "utils/updateUnfinishedRequest";
import finalizeRequest from "utils/finalizeRequest";

const Summary = () => {
  const t = usePhase("summary");
  const tIntro = usePhase("intro");
  const store = useStore();
  const setFromFetch = useStore((state) => state.setFromFetch);
  const setDraftFromAutoSave = useStore((state) => state.setDraftFromAutoSave);

  let navigate = useNavigate();

  let { hash } = useParams();

  const [shareDataLoaded, setShareDataLoaded] = useState(false);
  const [alertModalIsOpen, setAlertModalIsOpen] = useState(false);

  const submitRef = useRef<HTMLDivElement>(null);

  const orderedNameAndEmailExists = store.ordererEmail && store.ordererName;

  useEffect(() => {
    if (hash && !shareDataLoaded) {
      fetchUnfinishedRequest(hash, (result) => {
        if (result.success) {
          setShareDataLoaded(true);
          setFromFetch(hash!, result.submission);
          navigate("/yhteenveto/vaihe-2");
        } else {
          navigate("/");
        }
      });
    }
  }, [hash, setFromFetch, setShareDataLoaded, shareDataLoaded, navigate]);

  useEffect(() => {
    if (!hash && !store.draftHash) {
      submitUnfinishedRequest(
        {
          version: 1,
          cost: store.calculateCost(),
          agency: store.agency,
          agencyIsForced: store.agencyIsForced,
          attendTransport: store.attendTransport,
          blessingPlace: store.blessingPlace,
          burial: store.burial,
          burialPlace: store.burialPlace,
          burialWishes: store.burialWishes,
          cemetery: store.cemetery,
          clothing: store.clothing,
          coffin: store.coffin,
          coffinFlower: store.coffinFlower,
          coffinFlowerSizes: store.coffinFlowerSizes,
          coffinOptions: store.coffinOptions,
          currentMunicipality: store.currentMunicipality,
          datetime: store.datetime,
          deceasedChurch: store.deceasedChurch,
          deceasedName: store.deceasedName,
          deceasedSocialId: store.deceasedSocialId,
          deceasedWishes: store.deceasedWishes,
          eventType: store.eventType,
          guestFlower: store.guestFlower,
          hospital: store.hospital,
          moreInfoAboutBurial: store.moreInfoAboutBurial,
          oldPlaceInfo: store.oldPlaceInfo,
          ordererName: null,
          ordererEmail: null,
          ordererPhone: null,
          ordererRelationship: null,
          package: store.package,
          servicesOffer: store.servicesOffer,
          stoneOffer: store.stoneOffer,
          targetMunicipality: store.targetMunicipality,
          transportationCost: store.transportationCost,
          transportationOutOfBounds: store.transportationOutOfBounds,
          urn: store.urn,
          urnFlower: store.urnFlower,
          whoReserves: store.whoReserves,
        },
        (result) => {
          if (result.success) setDraftFromAutoSave(result.hash);
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hash && !shareDataLoaded) {
    return <div>{/* <Loading /> */}</div>;
  }

  const data = {
    version: 1,
    acceptedPrivacyPolicy: store.acceptedPrivacyPolicy,
    cost: store.calculateCost(),
    agency: store.agency,
    agencyIsForced: store.agencyIsForced,
    attendTransport: store.attendTransport,
    blessingPlace: store.blessingPlace,
    burial: store.burial,
    burialPlace: store.burialPlace,
    burialWishes: store.burialWishes,
    cemetery: store.cemetery,
    clothing: store.clothing,
    coffin: store.coffin,
    coffinFlower: store.coffinFlower,
    coffinFlowerSizes: store.coffinFlowerSizes,
    coffinOptions: store.coffinOptions,
    currentMunicipality: store.currentMunicipality,
    datetime: store.datetime,
    deceasedChurch: store.deceasedChurch,
    deceasedName: store.deceasedName,
    deceasedSocialId: store.deceasedSocialId,
    deceasedWishes: store.deceasedWishes,
    guestFlower: store.guestFlower,
    eventType: store.eventType,
    hospital: store.hospital,
    moreInfoAboutBurial: store.moreInfoAboutBurial,
    oldPlaceInfo: store.oldPlaceInfo,
    ordererEmail: store.ordererEmail,
    ordererName: store.ordererName,
    ordererPhone: store.ordererPhone,
    ordererRelationship: store.ordererRelationship,
    package: store.package,
    servicesOffer: store.servicesOffer,
    stoneOffer: store.stoneOffer,
    targetMunicipality: store.targetMunicipality,
    transportationCost: store.transportationCost,
    transportationOutOfBounds: store.transportationOutOfBounds,
    urn: store.urn,
    urnFlower: store.urnFlower,
    whoReserves: store.whoReserves,
  };

  const buildDiff = (): Record<string, any> | null => {
    const snap = store.draftSnapshot;
    if (!snap || !store.draftHash) return null;

    const excludeFromDiff = new Set(["draftHash", "recommendedUrns"]);
    let hasRealChanges = false;
    const snapshotDiff: Record<string, any> = {};

    (Object.keys(snap) as (keyof typeof snap)[])
      .filter((key) => !excludeFromDiff.has(key))
      .forEach((key) => {
        const current = (store as any)[key];
        const changed = JSON.stringify(current) !== JSON.stringify(snap[key]);
        snapshotDiff[key] = changed ? current : null;
        if (changed) hasRealChanges = true;
      });

    if (!hasRealChanges) return null;

    return {
      hash: store.draftHash,
      version: data.version,
      cost: data.cost,
      acceptedPrivacyPolicy: store.acceptedPrivacyPolicy,
      ...snapshotDiff,
    };
  };

  const diffData = buildDiff();

  return (
    <>
      <div className="App-content">
        <div className={styles.title}>
          <h2 className="h2">{constants.summary}</h2>
          <div className={styles.total}>{t("checksummary")}</div>
          <Button fullParentWidth
            onClick={() => {
              if (submitRef.current) {
                const rect = submitRef.current.getBoundingClientRect();
                const targetY = rect.top + window.scrollY;
                const targetX = window.scrollX;

                window.scrollTo({
                  top: targetY - 60,
                  left: targetX,
                  behavior: 'smooth'
                });
              }
            }}
            id="button-edit-offer"
          >
            {t("scroll_to_send_offer")}
          </Button>
        </div>

        <Transportation />
        <Burial />
        <Coffin />
        <Urn />
        <Preparation />
        <Flowers />
        <Deceased />
        <div ref={submitRef} />
        <Contact />
        <div className={[styles.block, styles.notopmargin, styles.nobottommargin, styles.accept_box].join(" ")}>
          <p>{tIntro("terms_and_conditions_description")}</p>
        </div>
        <div className={[styles.block, styles.notopmargin, styles.accept_box].join(" ")}>
          <input
            id="tac"
            type="checkbox"
            name="terms-and-conditions"
            onChange={(e) =>
              store.setUserSelection(
                "acceptedPrivacyPolicy",
                e.target.checked ? Date.now() : null
              )
            }
            // acceptedPrivacyPolicy isn't fetched from the server, so this is always false on load
            checked={!!store.acceptedPrivacyPolicy}
          />
          <label htmlFor="tac">{tIntro("terms_and_conditions_agreement")}</label>
          <a href="/tietosuojaseloste" target="_blank">
            {tIntro("read_terms_and_conditions_agreement")}
          </a>
        </div>
        <div className={[styles.submit_box, styles.notopmargin].join(" ")}>
          <Buttons>
            <Button
              onClick={() => {
                if (!orderedNameAndEmailExists) {
                  setAlertModalIsOpen(true);
                  return;
                }
                if (diffData) {
                  updateUnfinishedRequest(diffData, (res) => {
                    if (res.success) finalizeRequest(store.draftHash!, (finalRes) => {
                      if (finalRes.success) navigate("/kiitos");
                    });
                  });
                } else if (store.draftHash) {
                  finalizeRequest(store.draftHash, (finalRes) => {
                    if (finalRes.success) navigate("/kiitos");
                  });
                } else {
                  sendOffer(data, (res) => {
                    navigate("/kiitos");
                  });
                }
              }}
              disabled={!store.acceptedPrivacyPolicy}
              id="button-send-offer"
            >
              {constants.send}
            </Button>
            <SendPdfButton data={data} diffData={diffData ?? undefined} draftHash={store.draftHash} />
          </Buttons>
        </div>

        <ShareSummary data={data} diffData={diffData ?? undefined} draftHash={store.draftHash} />
        <Previous to="/yhteenveto" />
      </div>
      <AnimatePresence>
        {alertModalIsOpen && (
          <Modal
            closeModal={() => {
              setAlertModalIsOpen(false);
            }}
          >
            <div className={styles.alertModalTitle}>
              {t("remember_to_add_orderer_email")}
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

export default Summary;
